import { updateMessageStatus } from "../redux/Slice/Inbox/InboxMessageSlice";
import { getDispatch } from "./socketSharedState";

// Lazily proxies to window.store (see redux/store.js) instead of a static import.
// store.js -> authSlice.js -> socketHandler.js -> this file already form an import
// cycle; importing store here too caused an intermittent "Cannot access
// '__WEBPACK_DEFAULT_EXPORT__' before initialization" crash depending on which
// module the bundler happened to touch first. Every call site below only ever
// calls store.getState() as a plain closure (no `this` reliance), so a lazy
// proxy is a drop-in replacement for the static import.
const store = new Proxy({}, {
  get: (_, prop) => (typeof window !== 'undefined' && window.store ? window.store[prop] : undefined),
});

// ─── pending message state ────────────────────────────────────────────────────
export const pendingMessages = new Map();
export const processedMessageIds = new Set();

let pendingMessagesIntervalId = null;

// ─── public API ───────────────────────────────────────────────────────────────
export const trackPendingMessage = (tempId, contactId, isInternalMention = false) => {
  const pendingData = {
    tempId,
    contactId,
    timestamp: Date.now(),
    isInternalMention,
    status: isInternalMention ? "internal" : undefined,
  };
  pendingMessages.set(tempId, pendingData);
  console.log(`📋 [TRACK PENDING] Added message to pending:`, {
    tempId, contactId, isInternalMention, status: pendingData.status, totalPending: pendingMessages.size
  });
};

export const deletePendingMessage = (tempId) => pendingMessages.delete(tempId);
export const hasPendingMessage = (tempId) => pendingMessages.has(tempId);

// ─── interval management ──────────────────────────────────────────────────────
export const cleanupPendingMessagesInterval = () => {
  if (pendingMessagesIntervalId !== null) {
    clearInterval(pendingMessagesIntervalId);
    pendingMessagesIntervalId = null;
  }
};

export const setupPendingMessagesInterval = () => {
  // Clear any existing interval before creating a new one (handles reconnect)
  cleanupPendingMessagesInterval();

  pendingMessagesIntervalId = setInterval(() => {
    const now = Date.now();
    const MESSAGE_TIMEOUT = 45000;

    pendingMessages.forEach((message, tempId) => {
      let shouldProcess = true;

      try {
        if (store && typeof store.getState === 'function') {
          const state = store.getState();
          const currentContactId = state?.inboxMessage?.messages?.contactID;
          const messages = state?.inboxMessage?.messages?.chats || [];

          const normalizedTempId = String(tempId);
          const findMsg = (msgs) => msgs.find(msg => {
            const msgId = msg.id ? String(msg.id) : null;
            const msgTempId = msg.tempId ? String(msg.tempId) : null;
            const msg_Id = msg._id ? String(msg._id) : null;
            return msgId === normalizedTempId || msgTempId === normalizedTempId || msg_Id === normalizedTempId;
          });

          const contactMatches =
            (currentContactId && message.contactId && String(currentContactId) === String(message.contactId)) ||
            (!currentContactId && messages.length > 0);

          if (contactMatches || messages.length > 0) {
            const existingMessage = findMsg(messages);
            if (existingMessage) {
              pendingMessages.delete(tempId);
              shouldProcess = false;
            }
          }
        }
      } catch (_) {}

      if (!shouldProcess) return;

      const isInternalMention = message.isInternalMention || message.status === "internal";
      if (isInternalMention) {
        pendingMessages.delete(tempId);
        return;
      }

      const messageAge = now - message.timestamp;
      if (messageAge <= MESSAGE_TIMEOUT) return;

      const MAX_AGE_BEFORE_ERROR = 180000;
      let shouldDispatch = true;

      try {
        if (store && typeof store.getState === 'function') {
          const state = store.getState();
          const messages = state?.inboxMessage?.messages?.chats || [];
          const normalizedTempId = String(tempId);
          const existingMessage = messages.find(msg => {
            const msgId = msg.id ? String(msg.id) : null;
            const msgTempId = msg.tempId ? String(msg.tempId) : null;
            const msg_Id = msg._id ? String(msg._id) : null;
            return msgId === normalizedTempId || msgTempId === normalizedTempId || msg_Id === normalizedTempId;
          });
          if (existingMessage) {
            pendingMessages.delete(tempId);
            shouldDispatch = false;
          }
        }
      } catch (_) {}

      if (shouldDispatch) {
        const dispatchFn = getDispatch();
        if (dispatchFn && typeof dispatchFn === 'function') {
          dispatchFn(updateMessageStatus({
            contactId: message.contactId,
            messageId: tempId,
            status: "sent",
            timestamp: new Date().toISOString()
          }));
        }
        if (messageAge > MAX_AGE_BEFORE_ERROR) {
          pendingMessages.delete(tempId);
        }
      }
    });
  }, 15000);
};
